import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency } from '../../lib/utils';
import { useWholesale, useDeleteWholesale } from '../../hooks/useWholesale';

export function WholesaleList() {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isError } = useWholesale();
  const deleteMutation = useDeleteWholesale();

  const handleDelete = async (id: number, nombre: string) => {
    if (!confirm(`¿Estás seguro de que quieres eliminar "${nombre}" de mayoristas?`)) return;
    deleteMutation.mutate(id);
  };

  const precios = data?.data || [];
  const preciosFiltrados = precios.filter((item: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.nombreProducto?.toLowerCase().includes(searchLower) ||
      item.codigoBarras?.toLowerCase().includes(searchLower) ||
      item.tipo?.toLowerCase().includes(searchLower) ||
      item.proveedor?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando precios mayoristas...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los precios mayoristas
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Precios Mayoristas</h1>
          <p className="text-sm text-gray-500">
            {preciosFiltrados.length} producto(s) con precio mayorista
          </p>
        </div>
        <Link to="/mayoristas/nuevo">
          <Button>
            <Plus size={18} />
            Nuevo Precio Mayorista
          </Button>
        </Link>
      </div>

      {/* Búsqueda */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por producto, código o tipo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Producto</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Tipo</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Precio</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Precio Descuento</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Precio Volumen</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Stock</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {preciosFiltrados.map((item: any) => {
                const isLowStock = item.stockActual <= item.stockMinimo && item.stockMinimo > 0;
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{item.nombreProducto}</p>
                      {item.codigoBarras && (
                        <p className="text-xs text-gray-400">Código: {item.codigoBarras}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{item.tipo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      {formatCurrency(item.precio || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {item.precioDescuento ? formatCurrency(item.precioDescuento) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {item.precioVolumen ? formatCurrency(item.precioVolumen) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`font-medium ${isLowStock ? 'text-red-500' : 'text-gray-700'}`}>
                        {item.stockActual || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isLowStock ? (
                        <Badge variant="danger" size="sm">
                          <AlertCircle size={12} className="mr-1" />
                          Stock Bajo
                        </Badge>
                      ) : item.stockActual === 0 ? (
                        <Badge variant="danger" size="sm">Agotado</Badge>
                      ) : (
                        <Badge variant="success" size="sm">Disponible</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <Link
                          to={`/mayoristas/editar/${item.id}`}
                          className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Edit size={16} />
                        </Link>
                        <button
                          onClick={() => handleDelete(item.id, item.nombreProducto)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {preciosFiltrados.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron precios mayoristas</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay precios mayoristas que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/mayoristas/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Agregar el primer precio mayorista
          </Link>
        </div>
      )}
    </div>
  );
}